(function () {
  'use strict';

  class SkioBabInline {
    constructor(el) {
      this.el = el;
      this.cart = [];
      this.boxData = null;
      this.minSize = 2;
      this.maxSize = 4;
      this.boxSizes = [];
      this.discounts = {};

      const configEl = this.el.querySelector('.skio-bab-config');
      this.config = configEl ? JSON.parse(configEl.textContent) : {};

      this.init();
    }

    async init() {
      try {
        const res = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
          method: 'POST',
          body: JSON.stringify({ productPlatformId: this.config.productId }),
        });
        const data = await res.json();
        if (!data.ok) throw new Error('Failed to fetch box data');
        this.boxData = data.DynamicBox;

        this.discounts = this.boxData.percentDiscountTier || {};
        this.boxSizes = this.boxData.sizeRange || this.boxData.sizeInterval || [2];
        this.minSize = this.boxSizes[0];
        this.maxSize = this.boxSizes[this.boxSizes.length - 1];
        if (this.boxData.sizeRange) {
          this.boxSizes = Array.from({ length: this.maxSize - this.minSize + 1 }, (_, k) => k + this.minSize);
        }

        this.selectedSellingPlan = null;
        this.renderHeader();
        this.renderTiers();
        this.renderProducts();
        this.renderFrequencyOptions();
        this.setupMobileBar();
        this.updateAll();
        this.setupMobileVisibility();
        this.el.classList.remove('is-loading');
      } catch (err) {
        console.error('Skio BAB init error:', err);
      }
    }

    renderHeader() {
      const h2 = this.el.querySelector('.skio-bab-header h2');
      const sub = this.el.querySelector('.skio-bab-subheader');
      if (h2) h2.textContent = this.boxData.settings.header || 'Build Your Custom Box';
      if (sub) sub.innerHTML = 'Choose at least <strong>' + this.minSize + '</strong> items for your box';
    }

    renderTiers() {
      const container = this.el.querySelector('.skio-bab-tiers');
      if (!container) return;
      const sizes = Object.keys(this.discounts).map(Number).sort((a, b) => a - b);
      if (sizes.length === 0) return;

      let html = '';
      sizes.forEach((size, i) => {
        if (i > 0) html += '<div class="skio-bab-tier-line"></div>';
        html += '<div class="skio-bab-tier-step" data-tier="' + size + '">' +
          '<div class="skio-bab-tier-check"><svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#F1CD7B"/></svg></div>' +
          '<div class="skio-bab-tier-num">' + size +
          '<span class="skio-bab-tier-label">' + this.discounts[size] + '% off</span>' +
          '</div></div>';
      });
      container.innerHTML = html;
    }

    renderProducts() {
      const grid = this.el.querySelector('.skio-bab-grid');
      if (!grid) return;

      const products = this.boxData.selectableProductVariants;
      const variantOrder = this.boxData.settings.productVariantOrder || [];
      const productOrder = this.boxData.settings.productOrder || [];
      let orderedProducts = [];

      if (variantOrder.length > 0) {
        const seen = new Set();
        variantOrder.forEach(vid => {
          const p = products.find(pr => pr.productVariants.some(v => v.platformId.split('/').pop() === vid));
          if (p && !seen.has(p.productPlatformId)) {
            seen.add(p.productPlatformId);
            orderedProducts.push(p);
          }
        });
      } else if (productOrder.length > 0) {
        productOrder.forEach(pid => {
          const p = products.find(pr => pr.productPlatformId.split('/').pop() === pid);
          if (p) orderedProducts.push(p);
        });
      } else {
        orderedProducts = [...products];
      }

      let html = '';
      orderedProducts.forEach(product => {
        let variants = product.productVariants;
        if (variantOrder.length > 0) {
          const ordered = [];
          variantOrder.forEach(vid => {
            const v = variants.find(pv => pv.platformId.split('/').pop() === vid);
            if (v) ordered.push(v);
          });
          variants.forEach(v => { if (!ordered.includes(v)) ordered.push(v); });
          variants = ordered;
        }

        variants.forEach(variant => {
          const image = variant.imageSrc || product.productImageSrc;
          const title = product.productTitle;
          const variantId = variant.platformId.split('/').pop();
          let variantTitle = variant.productVariantTitle === 'Default Title' || !variant.productVariantTitle
            ? '' : variant.productVariantTitle.replace(/[()]/g, '').trim();
          const price = this.formatMoney(variant.price * 100);

          const productId = product.productPlatformId.split('/').pop();

          html += '<div class="skio-bab-card" data-variant-id="' + variantId + '" ' +
            'data-product-handle="' + product.productHandle + '" ' +
            'data-product-id="' + productId + '" ' +
            'data-price="' + variant.price + '" ' +
            'data-title="' + this.escapeAttr(title) + '" ' +
            'data-variant-title="' + this.escapeAttr(variantTitle) + '" ' +
            'data-image="' + this.escapeAttr(image) + '">' +
            '<div class="skio-bab-card__img-wrap">' +
            '<img src="' + image + '&width=360&height=360" alt="' + this.escapeAttr(title) + '" loading="lazy" width="360" height="360">' +
            '</div>' +
            '<h3 class="skio-bab-card__title">' + this.escapeHtml(title) + '</h3>' +
            '<div class="skio-bab-card__reviews"><span class="junip-product-summary" data-product-id="' + productId + '"></span></div>' +
            (variantTitle ? '<div class="skio-bab-card__variant">' + this.escapeHtml(variantTitle) + '</div>' : '<div class="skio-bab-card__variant">&ndash;</div>') +
            '<div class="skio-bab-card__price">' + price + '</div>' +
            // ADD button (shown when not added)
            '<button class="skio-bab-card__btn skio-bab-card__add" type="button">ADD</button>' +
            // Qty controls (shown when added)
            '<div class="skio-bab-card__qty" style="display:none;">' +
            '<button class="skio-bab-qty-btn skio-bab-card-qty-minus" type="button">&ndash;</button>' +
            '<span class="skio-bab-card__qty-val">1</span>' +
            '<button class="skio-bab-qty-btn skio-bab-card-qty-plus" type="button">+</button>' +
            '</div>' +
            '</div>';
        });
      });
      grid.innerHTML = html;

      // Bind ADD buttons
      grid.querySelectorAll('.skio-bab-card__add').forEach(btn => {
        btn.addEventListener('click', () => this.addItem(btn.closest('.skio-bab-card')));
      });

      // Bind card qty buttons
      grid.querySelectorAll('.skio-bab-card-qty-minus').forEach(btn => {
        btn.addEventListener('click', () => {
          const card = btn.closest('.skio-bab-card');
          this.changeQty(card.dataset.variantId, -1);
        });
      });
      grid.querySelectorAll('.skio-bab-card-qty-plus').forEach(btn => {
        btn.addEventListener('click', () => {
          const card = btn.closest('.skio-bab-card');
          this.changeQty(card.dataset.variantId, 1);
        });
      });

      // Re-init Junip to render stars on dynamically added cards
      if (window.Junip && window.Junip.init) window.Junip.init();
    }

    addItem(card) {
      const totalQty = this.cart.reduce((sum, i) => sum + i.qty, 0);
      if (totalQty >= this.maxSize) return;

      this.cart.push({
        variantId: card.dataset.variantId,
        productHandle: card.dataset.productHandle,
        price: parseFloat(card.dataset.price),
        title: card.dataset.title,
        variantTitle: card.dataset.variantTitle,
        image: card.dataset.image,
        qty: 1,
      });
      this.updateAll();
    }

    changeQty(variantId, delta) {
      const item = this.cart.find(i => i.variantId === variantId);
      if (!item) return;

      const totalQty = this.cart.reduce((sum, i) => sum + i.qty, 0);
      if (delta > 0 && totalQty >= this.maxSize) return;

      item.qty += delta;
      if (item.qty <= 0) {
        this.cart = this.cart.filter(i => i.variantId !== variantId);
      }
      this.updateAll();
    }

    updateAll() {
      this.updateCards();
      this.updateSidebar();
      this.updateMobileBar();
    }

    updateCards() {
      const totalQty = this.cart.reduce((sum, i) => sum + i.qty, 0);
      const cartMap = {};
      this.cart.forEach(i => { cartMap[i.variantId] = i.qty; });

      this.el.querySelectorAll('.skio-bab-card').forEach(card => {
        const vid = card.dataset.variantId;
        const addBtn = card.querySelector('.skio-bab-card__add');
        const qtyWrap = card.querySelector('.skio-bab-card__qty');
        const qtyVal = card.querySelector('.skio-bab-card__qty-val');

        if (cartMap[vid]) {
          card.classList.add('is-added');
          card.classList.remove('is-disabled');
          addBtn.style.display = 'none';
          qtyWrap.style.display = '';
          qtyVal.textContent = cartMap[vid];
        } else if (totalQty >= this.maxSize) {
          card.classList.remove('is-added');
          card.classList.add('is-disabled');
          addBtn.style.display = '';
          qtyWrap.style.display = 'none';
        } else {
          card.classList.remove('is-added', 'is-disabled');
          addBtn.style.display = '';
          qtyWrap.style.display = 'none';
        }
      });
    }

    updateSidebar() {
      const totalQty = this.cart.reduce((sum, i) => sum + i.qty, 0);

      // Count badge
      const countEl = this.el.querySelector('.skio-bab-count');
      if (countEl) countEl.textContent = totalQty + ' item' + (totalQty !== 1 ? 's' : '') + ' selected';

      // Empty / list toggle
      const emptyEl = this.el.querySelector('.skio-bab-empty');
      const listEl = this.el.querySelector('.skio-bab-items-list');
      if (emptyEl) emptyEl.style.display = totalQty === 0 ? '' : 'none';
      if (listEl) {
        listEl.style.display = totalQty === 0 ? 'none' : '';
        let html = '';
        this.cart.forEach(item => {
          html += '<div class="skio-bab-item">' +
            '<div class="skio-bab-item__img"><img src="' + item.image + '&width=80&height=80" alt="" width="40" height="40"></div>' +
            '<div class="skio-bab-item__info">' +
            '<div class="skio-bab-item__title">' + this.escapeHtml(item.title) + '</div>' +
            '<div class="skio-bab-item__variant">' + (this.escapeHtml(item.variantTitle) || '') + '</div>' +
            '</div>' +
            '<div class="skio-bab-item__qty">' +
            '<button class="skio-bab-qty-btn skio-bab-sidebar-qty-minus" type="button" data-vid="' + item.variantId + '">&ndash;</button>' +
            '<span>' + item.qty + '</span>' +
            '<button class="skio-bab-qty-btn skio-bab-sidebar-qty-plus" type="button" data-vid="' + item.variantId + '">+</button>' +
            '</div></div>';
        });
        listEl.innerHTML = html;

        listEl.querySelectorAll('.skio-bab-sidebar-qty-minus').forEach(btn => {
          btn.addEventListener('click', () => this.changeQty(btn.dataset.vid, -1));
        });
        listEl.querySelectorAll('.skio-bab-sidebar-qty-plus').forEach(btn => {
          btn.addEventListener('click', () => this.changeQty(btn.dataset.vid, 1));
        });
      }

      // Hint
      const hintEl = this.el.querySelector('.skio-bab-hint');
      if (hintEl) {
        if (totalQty < this.minSize) {
          const rem = this.minSize - totalQty;
          hintEl.innerHTML = 'Add ' + rem + ' more item' + (rem !== 1 ? 's' : '') + ' to get <strong>' + this.discounts[this.minSize] + '% off</strong>!';
          hintEl.style.display = '';
        } else if (totalQty < this.maxSize) {
          const next = totalQty + 1;
          const nd = this.discounts[next];
          hintEl.innerHTML = nd ? 'Add 1 more item to get <strong>' + nd + '% off</strong>!' : '';
          hintEl.style.display = nd ? '' : 'none';
        } else {
          hintEl.style.display = 'none';
        }
      }

      // Tiers
      const steps = this.el.querySelectorAll('.skio-bab-tier-step');
      const lines = this.el.querySelectorAll('.skio-bab-tier-line');
      steps.forEach((badge, i) => {
        const tier = parseInt(badge.dataset.tier);
        badge.classList.toggle('is-active', totalQty >= tier);
        if (i > 0 && lines[i - 1]) {
          lines[i - 1].classList.toggle('is-filled', totalQty >= tier);
        }
      });

      // Price
      let subtotal = 0;
      this.cart.forEach(item => { subtotal += item.price * item.qty; });
      const dp = this.discounts[totalQty];
      let finalPrice = subtotal;
      let totalDiscountPercent = 0;
      if (dp) {
        totalDiscountPercent += parseFloat(dp);
      }

      // Subscription discount
      if (this.selectedSellingPlan) {
        const activeOpt = this.el.querySelector('.skio-bab-freq-option.is-active[data-plan]');
        if (activeOpt && activeOpt.dataset.discountType === 'PERCENTAGE') {
          totalDiscountPercent += parseFloat(activeOpt.dataset.discountAmount || 0);
        }
      }

      if (totalDiscountPercent > 0) {
        finalPrice = subtotal - (subtotal * totalDiscountPercent / 100);
      }

      // (per-option prices are calculated below)

      // Update price on ALL frequency options with crossed out original
      this.el.querySelectorAll('.skio-bab-freq-option').forEach(opt => {
        const priceSpan = opt.querySelector('.skio-bab-freq-price');
        if (!priceSpan || totalQty === 0) {
          if (priceSpan) priceSpan.innerHTML = '';
          return;
        }
        let optDiscount = dp ? parseFloat(dp) : 0;
        if (opt.dataset.discountType === 'PERCENTAGE') {
          optDiscount += parseFloat(opt.dataset.discountAmount || 0);
        }
        const optPrice = subtotal - (subtotal * optDiscount / 100);
        if (optDiscount > 0) {
          priceSpan.innerHTML = '<span class="skio-bab-freq-price-original">' + this.formatMoney(subtotal * 100) + '</span> ' + this.formatMoney(optPrice * 100);
        } else {
          priceSpan.innerHTML = this.formatMoney(optPrice * 100);
        }
      });

      // Checkout button
      const atcBtn = this.el.querySelector('.skio-bab-checkout');
      if (atcBtn) {
        if (totalQty >= this.minSize) {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Checkout';
        } else {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Add at least ' + this.minSize + ' item(s)';
        }
      }
    }

    // ===== FREQUENCY / SELLING PLANS =====
    renderFrequencyOptions() {
      const spg = this.boxData.sellingPlanGroup;
      const containers = this.el.querySelectorAll('.skio-bab-freq-options');
      if (!containers.length) return;

      containers.forEach(container => {
        let html = '';

        // One-time option
        html += '<div class="skio-bab-freq-option is-active" data-plan="">' +
          '<div class="skio-bab-freq-radio"></div>' +
          '<span>One-time Purchase</span>' +
          '<span class="skio-bab-freq-price"></span>' +
          '</div>';

        // Subscription options from Skio
        if (spg) {
          // Log for debugging — remove once confirmed
          console.log('Skio sellingPlanGroup:', JSON.stringify(spg, null, 2));

          Object.entries(spg).forEach(([planId, plan]) => {
            const id = planId.split('/').pop();
            let discount = '';
            if (plan.priceAdjustmentType === 'PERCENTAGE') {
              discount = plan.priceAdjustmentAmount + '% off';
            }

            // Build label with frequency info
            let label = '';
            if (plan.name) {
              label = plan.name;
            } else if (plan.intervalCount && plan.interval) {
              label = 'Every ' + plan.intervalCount + ' ' + plan.interval.toLowerCase() + (plan.intervalCount > 1 ? 's' : '');
            } else if (plan.deliveryFrequency) {
              label = plan.deliveryFrequency;
            } else if (plan.frequencyLabel) {
              label = plan.frequencyLabel;
            } else {
              label = 'Subscribe & Save';
            }

            html += '<div class="skio-bab-freq-option" data-plan="' + id + '" data-discount-type="' +
              (plan.priceAdjustmentType || '') + '" data-discount-amount="' + (plan.priceAdjustmentAmount || 0) + '">' +
              '<div class="skio-bab-freq-radio"></div>' +
              '<span class="skio-bab-freq-label-wrap">' +
              '<strong>Subscribe & Save' + (discount ? ' ' + discount : '') + '</strong>' +
              '<span class="skio-bab-freq-sublabel">' + label + '</span>' +
              '</span>' +
              '<span class="skio-bab-freq-price"></span>' +
              '</div>';
          });
        }

        container.innerHTML = html;

        // Bind click handlers
        container.querySelectorAll('.skio-bab-freq-option').forEach(opt => {
          opt.addEventListener('click', () => {
            container.querySelectorAll('.skio-bab-freq-option').forEach(o => o.classList.remove('is-active'));
            opt.classList.add('is-active');
            this.selectedSellingPlan = opt.dataset.plan || null;
            this.updateAll();
          });
        });
      });
    }

    // ===== MOBILE STICKY DRAWER =====
    setupMobileBar() {
      const bar = this.el.querySelector('.skio-bab-mobile-bar');
      if (!bar) return;

      const detailsBtn = bar.querySelector('.skio-bab-mobile-details');
      if (detailsBtn) {
        detailsBtn.addEventListener('click', () => {
          bar.classList.toggle('is-expanded');
          detailsBtn.classList.toggle('is-open');
        });
      }

      // Mobile checkout button
      const mobileCheckout = bar.querySelector('.skio-bab-mobile-checkout');
      if (mobileCheckout) {
        mobileCheckout.addEventListener('click', () => this.addToCart());
      }
    }

    updateMobileBar() {
      const bar = this.el.querySelector('.skio-bab-mobile-bar');
      if (!bar) return;

      const totalQty = this.cart.reduce((sum, i) => sum + i.qty, 0);

      // Count
      const countEl = bar.querySelector('.skio-bab-mobile-count');
      if (countEl) countEl.textContent = totalQty + ' item' + (totalQty !== 1 ? 's' : '') + ' selected';

      // Hint
      const hintEl = bar.querySelector('.skio-bab-mobile-hint');
      if (hintEl) {
        if (totalQty === 0) {
          hintEl.innerHTML = '';
        } else if (totalQty < this.minSize) {
          const rem = this.minSize - totalQty;
          hintEl.innerHTML = 'Add ' + rem + ' more item' + (rem !== 1 ? 's' : '') + ' to get <strong>' + this.discounts[this.minSize] + '% off</strong>!';
        } else if (totalQty < this.maxSize) {
          const next = totalQty + 1;
          const nd = this.discounts[next];
          hintEl.innerHTML = nd ? 'Add 1 more item to get <strong>' + nd + '% off</strong>!' : '';
        } else {
          hintEl.innerHTML = 'Box is full!';
        }
      }

      // Show/hide bar
      bar.classList.toggle('is-visible', totalQty > 0);

      // === Expanded panel contents ===

      // Product thumbnails with qty badges
      const itemsEl = bar.querySelector('.skio-bab-mobile-items');
      if (itemsEl) {
        let html = '';
        this.cart.forEach(item => {
          html += '<div class="skio-bab-mobile-item">' +
            '<span class="skio-bab-mobile-item__qty">' + item.qty + '</span>' +
            '<img src="' + item.image + '&width=88&height=88" alt="" width="44" height="44">' +
            '<button class="skio-bab-mobile-item__remove" data-vid="' + item.variantId + '">&times;</button>' +
            '</div>';
        });
        itemsEl.innerHTML = html;

        itemsEl.querySelectorAll('.skio-bab-mobile-item__remove').forEach(btn => {
          btn.addEventListener('click', () => {
            this.cart = this.cart.filter(i => i.variantId !== btn.dataset.vid);
            // Also update the card
            const card = this.el.querySelector('.skio-bab-card[data-variant-id="' + btn.dataset.vid + '"]');
            if (card) {
              card.classList.remove('is-added');
              card.querySelector('.skio-bab-card__add').style.display = '';
              card.querySelector('.skio-bab-card__qty').style.display = 'none';
            }
            this.updateAll();
          });
        });
      }

      // Discount pills
      const discEl = bar.querySelector('.skio-bab-mobile-discounts');
      if (discEl) {
        let html = '<span class="skio-bab-mobile-disc-label">Discounts</span>';
        const sizes = Object.keys(this.discounts).map(Number).sort((a, b) => a - b);
        sizes.forEach(size => {
          html += '<span class="skio-bab-mobile-disc-label">' + this.discounts[size] + '% off</span>';
        });
        discEl.innerHTML = html;
      }

      // Price
      let subtotal = 0;
      this.cart.forEach(item => { subtotal += item.price * item.qty; });
      const dp = this.discounts[totalQty];
      let finalPrice = subtotal;
      if (dp) finalPrice = subtotal - (subtotal * parseFloat(dp) / 100);

      const mPriceEl = bar.querySelector('.skio-bab-mobile-freq-price');
      if (mPriceEl) mPriceEl.textContent = totalQty > 0 ? this.formatMoney(finalPrice * 100) : '';

      // Mobile checkout button
      const mCheckout = bar.querySelector('.skio-bab-mobile-checkout');
      if (mCheckout) {
        if (totalQty >= this.minSize) {
          mCheckout.disabled = false;
          mCheckout.textContent = 'Checkout';
        } else {
          mCheckout.disabled = true;
          mCheckout.textContent = 'Add ' + (this.minSize - totalQty) + ' more item(s)';
        }
      }
    }

    setupMobileVisibility() {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
      const main = this.el.querySelector('.skio-bab-grid');
      const sidebar = this.el.querySelector('.skio-bab-sidebar');
      if (!main || !sidebar) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!sidebar.classList.contains('is-expanded')) {
            // Don't auto-hide if user expanded it
          }
        });
      }, { threshold: 0 });
      observer.observe(main);
    }

    // ===== ADD TO CART =====
    async addToCart() {
      const atcBtn = this.el.querySelector('.skio-bab-checkout');
      if (!atcBtn || atcBtn.disabled) return;

      atcBtn.disabled = true;
      atcBtn.textContent = 'Adding...';

      try {
        const cartRes = await fetch('/cart.json').then(r => r.json());
        const boxIndexes = cartRes.items
          .filter(item => item.properties && item.properties._dynamicBoxIndex !== undefined)
          .map(item => item.properties._dynamicBoxIndex);
        const nextIndex = boxIndexes.length > 0 ? Math.max(...boxIndexes) + 1 : 1;

        const items = [];
        this.cart.forEach(item => {
          const cartItem = {
            id: parseInt(item.variantId),
            quantity: item.qty,
            properties: {
              _dynamicBoxIndex: nextIndex,
              _dynamicBoxId: this.boxData.boxId,
            },
          };
          if (this.selectedSellingPlan) {
            cartItem.selling_plan = parseInt(this.selectedSellingPlan);
          }
          items.push(cartItem);
        });

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        }).then(r => r.json());

        // Handle post-add behavior
        if (this.config.afterAdd === 'checkout') {
          window.location.href = '/checkout';
          return;
        } else if (this.config.afterAdd === 'cart') {
          window.location.href = '/cart';
          return;
        } else if (this.config.showModal === 'true' || this.config.showModal === true) {
          this.showUpsellModal(addRes, nextIndex);
        } else {
          this.onCartUpdated();
        }
        // Reset bundle
        this.cart = [];
        this.updateAll();
      } catch (err) {
        console.error('Add to cart error:', err);
        atcBtn.disabled = false;
        atcBtn.textContent = 'Checkout';
      }
    }

    showUpsellModal(addedItems, boxIndex) {
      const modal = document.querySelector('.skio-bab-modal--upsell');
      if (!modal) { this.onCartUpdated(); return; }

      modal.setAttribute('open', '');
      modal.style.cssText = 'opacity:1;visibility:visible;z-index:2147483649;';
      document.body.classList.add('overflow-hidden');

      const closeModal = () => {
        modal.removeAttribute('open');
        modal.style.cssText = 'opacity:0;visibility:hidden;z-index:-1;';
        document.body.classList.remove('overflow-hidden');
        this.onCartUpdated();
      };

      const dismissBtn = modal.querySelector('.skio-bab-modal-dismiss');
      if (dismissBtn) dismissBtn.addEventListener('click', closeModal, { once: true });
      const closeBtn = modal.querySelector('.skio-bab-modal-close');
      if (closeBtn) closeBtn.addEventListener('click', closeModal, { once: true });
      modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); }, { once: true });

      const subBtn = modal.querySelector('.skio-bab-modal-subscribe');
      if (subBtn) {
        subBtn.addEventListener('click', async () => {
          const spg = this.boxData.sellingPlanGroup;
          if (!spg) { closeModal(); return; }
          const spId = Object.keys(spg).shift().split('/').pop();

          const cartJson = await fetch('/cart.js').then(r => r.json());
          const boxItems = cartJson.items.filter(i => i.properties && i.properties._dynamicBoxIndex === boxIndex);
          const remove = {};
          const add = { items: [] };
          boxItems.forEach(item => {
            remove[item.key] = 0;
            add.items.push({ id: item.id, properties: item.properties, quantity: item.quantity, selling_plan: spId });
          });

          await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: remove }) });
          await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(add) });

          modal.removeAttribute('open');
          modal.style.cssText = 'opacity:0;visibility:hidden;z-index:-1;';
          document.body.classList.remove('overflow-hidden');
          this.onCartUpdated();
        }, { once: true });
      }
    }

    onCartUpdated() {
      // Dispatch events for cart drawers (Rebuy, Eurus/Alpine, etc.)
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('rebuy:cart:change'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Eurus/Alpine cart state refresh
      if (window.Alpine && window.Alpine.store) {
        const store = window.Alpine.store('cart');
        if (store && store.refresh) store.refresh();
      }

      // Redirect based on setting
      if (this.config.afterAdd === 'checkout') {
        window.location.href = '/checkout';
      } else if (this.config.afterAdd === 'cart') {
        window.location.href = '/cart';
      }
      // 'stay' = do nothing, let cart drawer handle it
    }

    formatMoney(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const d = (cents / 100).toFixed(2).split('.');
      return '$' + d[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + '.' + d[1];
    }

    escapeHtml(s) {
      return s ? s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
    }

    escapeAttr(s) {
      return s ? s.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/'/g,'&#39;').replace(/</g,'&lt;').replace(/>/g,'&gt;') : '';
    }
  }

  // Init
  function initSkioBab(scope) {
    (scope || document).querySelectorAll('.skio-bab-wrapper:not([data-initialized])').forEach(el => {
      el.setAttribute('data-initialized', 'true');
      const instance = new SkioBabInline(el);
      const checkoutBtn = el.querySelector('.skio-bab-checkout');
      if (checkoutBtn) checkoutBtn.addEventListener('click', () => instance.addToCart());
    });
  }

  document.addEventListener('DOMContentLoaded', () => initSkioBab());

  // Reset buttons on back/forward navigation (bfcache)
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      document.querySelectorAll('.skio-bab-checkout, .skio-bab-mobile-checkout').forEach(btn => {
        btn.disabled = false;
        btn.textContent = 'Checkout';
      });
    }
  });

  // Shopify theme editor: re-init when section is loaded/reloaded
  document.addEventListener('shopify:section:load', (e) => {
    initSkioBab(e.target);
  });
})();
